import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { aiForecastingService } from '../../services/aiForecastingService';
import SeasonDemandChart from '../../components/reports/SeasonDemandChart';
import SkuBreakdown, {
  SeasonChangeBadge,
  SkuOtherAnomalies,
} from '../../components/reports/SkuBreakdown';
import {
  ForecastSeriesResponse,
  HolidayKey,
  SeasonAnomalyProduct,
  SeasonViewResponse,
} from '../../types/AiForecasting';
import {
  season_sold_by_year,
  season_year_lines as format_season_year_lines,
  years_with_activity,
} from '../../components/reports/seasonChartUtils';
import { season_sku_wash_subtitle, season_wash_subtitle } from './ProductionForecastPage.wash';
import ForecastBulkTracker from '../../components/reports/ForecastBulkTracker';
import './ProductionForecastPage.css';

function season_as_series(view: SeasonViewResponse): ForecastSeriesResponse {
  return {
    extract_meta: view.extract_meta,
    holiday_windows: view.holiday_windows || [],
    product: {
      product_id: 0,
      product_code: view.holiday_key,
      name: `${view.holiday_label} · all products`,
      weeks: view.weeks,
    },
  };
}

const ProductionForecastPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: auth_loading } = useAuth();
  const { hasPermission, loading: permissions_loading } = usePermissions();
  const has_access =
    hasPermission('reports.view') ||
    Boolean(user && (user.role_id === 1 || user.role_name === 'Super Admin'));

  const [graph_ready, set_graph_ready] = useState(false);
  const [season_view, set_season_view] = useState<SeasonViewResponse | null>(null);
  const [holiday_key, set_holiday_key] = useState<HolidayKey | ''>('');
  const holiday_key_ref = useRef<HolidayKey | ''>('');
  holiday_key_ref.current = holiday_key;
  const [selected_product_id, set_selected_product_id] = useState<number | null>(null);
  const [product_series, set_product_series] = useState<ForecastSeriesResponse | null>(null);
  const [list_query, set_list_query] = useState('');
  const [loading, set_loading] = useState(true);
  const [extracting, set_extracting] = useState(false);
  const [bulk_reload_key, set_bulk_reload_key] = useState(0);
  const [error, set_error] = useState<string | null>(null);
  const list_item_refs = useRef<Map<number, HTMLLIElement>>(new Map());

  useEffect(() => {
    if (!auth_loading && !permissions_loading && !has_access) {
      navigate('/reports');
    }
  }, [auth_loading, permissions_loading, has_access, navigate]);

  const load_season = useCallback(async (key?: HolidayKey) => {
    const view = await aiForecastingService.getSeasonView(key);
    set_season_view(view);
    set_holiday_key(view.holiday_key);
    return view;
  }, []);

  const load_status = useCallback(async () => {
    set_loading(true);
    set_error(null);
    try {
      const status = await aiForecastingService.getStatus();
      set_graph_ready(status.graph_ready);
      if (!status.graph_ready) {
        set_season_view(null);
        set_product_series(null);
        return;
      }
      const keep_key = holiday_key_ref.current;
      await load_season(keep_key || undefined);
    } catch (err: unknown) {
      set_error(err instanceof Error ? err.message : 'Failed to load forecast data');
    } finally {
      set_loading(false);
    }
  }, [load_season]);

  useEffect(() => {
    if (has_access) {
      void load_status();
    }
  }, [has_access, load_status]);

  useEffect(() => {
    if (!selected_product_id || !graph_ready || !holiday_key) {
      if (!selected_product_id) {
        set_product_series(null);
      }
      return;
    }
    let cancelled = false;
    aiForecastingService
      .getSeries(selected_product_id, holiday_key)
      .then((result) => {
        if (!cancelled) {
          set_product_series(result);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          set_error(err instanceof Error ? err.message : 'Failed to load product series');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selected_product_id, graph_ready, holiday_key]);

  const season_series = useMemo(
    () => (season_view ? season_as_series(season_view) : null),
    [season_view]
  );
  const all_years = useMemo(
    () => (season_view ? years_with_activity(season_view.weeks) : []),
    [season_view]
  );
  const season_skus = season_view?.anomaly_products || [];
  const anomaly_rows = useMemo(() => {
    const q = list_query.trim().toLowerCase();
    if (!q) {
      return season_skus;
    }
    return season_skus.filter(
      (row) => row.name.toLowerCase().includes(q) || row.product_code.toLowerCase().includes(q)
    );
  }, [season_skus, list_query]);
  const season_year_lines = useMemo(() => {
    if (!season_view) {
      return [];
    }
    return format_season_year_lines(
      season_sold_by_year(season_view.weeks, season_view.holiday_key)
    );
  }, [season_view]);

  const selected_row =
    (season_view?.anomaly_products || []).find((row) => row.product_id === selected_product_id) ||
    null;
  const sku_breakdown = product_series?.breakdown || selected_row?.breakdown || null;
  const product_years = useMemo(
    () => (product_series ? years_with_activity(product_series.product.weeks) : []),
    [product_series]
  );

  useEffect(() => {
    if (selected_product_id == null) {
      return;
    }
    list_item_refs.current.get(selected_product_id)?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [selected_product_id, anomaly_rows]);

  const change_season = async (next_key: HolidayKey) => {
    set_holiday_key(next_key);
    set_selected_product_id(null);
    set_product_series(null);
    set_error(null);
    try {
      await load_season(next_key);
    } catch (err: unknown) {
      set_error(err instanceof Error ? err.message : 'Failed to load season');
    }
  };

  const select_anomaly = (row: SeasonAnomalyProduct) => {
    set_list_query('');
    set_selected_product_id(row.product_id);
  };

  const run_extract = async () => {
    set_extracting(true);
    set_error(null);
    try {
      await aiForecastingService.runExtract();
      set_selected_product_id(null);
      set_bulk_reload_key((key) => key + 1);
      await load_status();
    } catch (err: unknown) {
      set_error(err instanceof Error ? err.message : 'Extract failed');
    } finally {
      set_extracting(false);
    }
  };

  if (!auth_loading && !permissions_loading && !has_access) {
    return null;
  }

  return (
    <div className="forecast-page">
      <div className="forecast-page__header">
        <h1>Production forecast seasons</h1>
        <p>
          Pick a season, then a SKU. The graph compares years. To make = holiday need + regular
          months that fit usable shelf life − on hand.
        </p>
      </div>

      <div className="forecast-page__toolbar">
        <div className="forecast-page__field">
          <label htmlFor="forecast-season">Season</label>
          <select
            id="forecast-season"
            value={holiday_key}
            onChange={(e) => void change_season(e.target.value as HolidayKey)}
            disabled={!graph_ready || !season_view}
          >
            {(season_view?.available_seasons || []).map((season) => (
              <option key={season.holiday_key} value={season.holiday_key}>
                {season.holiday_label}
              </option>
            ))}
          </select>
        </div>
        <div className="forecast-page__actions">
          <button
            className="forecast-page__btn"
            type="button"
            onClick={run_extract}
            disabled={extracting}
          >
            {extracting ? 'Extracting…' : graph_ready ? 'Re-run extract' : 'Run extract'}
          </button>
        </div>
      </div>

      {season_view && (
        <p className="forecast-page__meta">
          Last extract {season_view.extract_meta.as_of.slice(0, 10)} · {season_view.holiday_label} ·{' '}
          {season_skus.length} season SKUs
        </p>
      )}

      {error && <p className="forecast-page__error">{error}</p>}
      {loading && <p className="forecast-page__empty">Loading…</p>}
      {!loading && !graph_ready && (
        <p className="forecast-page__empty">
          No saved pipeline files yet. Run extract to pull eligible products, weekly sales, and
          production once, then this graph will load from those files.
        </p>
      )}

      {season_series && season_view && (
        <section className="forecast-page__section">
          <SeasonDemandChart
            series={season_series}
            weeks={season_view.weeks}
            selected_years={all_years}
            mode="yoy"
            heading={`${season_view.holiday_label} versus the rest of the year`}
            subtitle={season_wash_subtitle(season_view)}
            show_full_year
            show_seasons
            show_legend
            highlight_holiday={season_view.holiday_key}
            holiday_label={season_view.holiday_label}
            anomaly_products={season_view.anomaly_products}
          />
          {season_year_lines.length > 0 && (
            <p className="forecast-page__season-totals">
              <strong>{season_view.holiday_label} sold across years:</strong>{' '}
              {season_year_lines.join(' · ')}
            </p>
          )}
        </section>
      )}

      {season_view && (
        <section className="forecast-page__section">
          <h2 className="forecast-page__row-title">{season_view.holiday_label} SKUs</h2>
          <div className="forecast-page__below">
            <div className="forecast-anomaly">
              <input
                className="forecast-anomaly__search"
                value={list_query}
                onChange={(e) => set_list_query(e.target.value)}
                placeholder="Filter code or name"
                aria-label="Filter anomaly items"
              />
              {anomaly_rows.length === 0 ? (
                <p className="season-chart__empty">
                  No products tagged to {season_view.holiday_label}.
                </p>
              ) : (
                <ul className="forecast-anomaly__list">
                  {anomaly_rows.map((row) => {
                    return (
                      <li
                        key={row.product_id}
                        ref={(el) => {
                          if (el) {
                            list_item_refs.current.set(row.product_id, el);
                          } else {
                            list_item_refs.current.delete(row.product_id);
                          }
                        }}
                      >
                        <button
                          type="button"
                          className={
                            row.product_id === selected_product_id
                              ? 'forecast-anomaly__item forecast-anomaly__item--selected'
                              : 'forecast-anomaly__item'
                          }
                          onClick={() => select_anomaly(row)}
                        >
                          <span className="forecast-anomaly__top">
                            <span className="forecast-anomaly__code">{row.product_code}</span>
                            <SeasonChangeBadge row={row} />
                          </span>
                          <span className="forecast-anomaly__name">{row.name}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {product_series && selected_row ? (
              <div className="forecast-sku-detail">
                <SeasonDemandChart
                  series={product_series}
                  weeks={product_series.product.weeks}
                  selected_years={product_years}
                  mode="yoy"
                  heading={`${selected_row.product_code} · each calendar year`}
                  subtitle={season_sku_wash_subtitle(season_view)}
                  show_legend
                  show_seasons
                  show_full_year
                  highlight_holiday={season_view.holiday_key}
                  holiday_label={season_view.holiday_label}
                  anomaly_products={[selected_row]}
                />
                {sku_breakdown ? (
                  <SkuBreakdown
                    row={sku_breakdown}
                    holiday_label={season_view.holiday_label}
                    change_row={selected_row}
                  />
                ) : (
                  <p className="forecast-page__recs-empty">
                    Not enough consecutive {season_view.holiday_label} years to compare for this
                    SKU.
                  </p>
                )}
                <SkuOtherAnomalies
                  hits={product_series.other_anomalies || []}
                  current_holiday_label={season_view.holiday_label}
                  sku_label={selected_row.product_code}
                />
              </div>
            ) : (
              <p className="season-chart season-chart__empty">
                Select a product from the list to see a line for each year.
              </p>
            )}
          </div>
        </section>
      )}

      <ForecastBulkTracker reload_key={bulk_reload_key} />
    </div>
  );
};

export default ProductionForecastPage;
